<template>
  <div class="bg-gray-21 p-3">
    <div class="d-flex justify-content-end">
      <action-button
        class="btn-warning-outline px-4 py-2 mr-2"
        :text="$t('GoBack')"
        i="fa fa-arrow-circle-left"
        @click="$router.go(-1)"
      />

      <action-button
        class="btn-success px-4 py-2"
        :text="$t('Save')"
        i="fa fa-save"
        @click="onSaveHandler"
      />
    </div>

    <validation-observer ref="formRef" v-slot="{}" tag="form">
      <!--User Account-->
      <div class="bg-white rounded p-4 mt-4">
        <div class="d-flex align-items-center">
          <em class="fal fa-ellipsis-h color-orange-light-2 mr-3 font-40" />
          <span class="color-dark-blue font-14 font-weight-bold">
            {{ $t('UserAccount') | upper }}
          </span>
        </div>
        <div class="row mt-2">
          <div v-if="!isNewProfile" class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Username') | capitalize }}:
            </div>
            <span class="color-black font-16">
              {{ model.username }}
            </span>
          </div>
          <div v-else class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('lblWizardStep2MentorEmail.text') | capitalize }}:
            </div>
            <validation-provider rules="required|email" v-slot="{ errors }">
              <custom-input
                v-model="model.email"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Role') }}:
            </div>
            <validation-provider
              v-if="isNewProfile"
              rules="required"
              v-slot="{ errors }"
            >
              <custom-drop-down
                label=""
                placeholder="Seleccione"
                :options="options.roles"
                fieldkey="id"
                fieldname="name"
                v-model="model.role"
                @input="() => {}"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
            <span v-else class="color-black font-16">
              {{ model.roleName }}
            </span>
          </div>
          <div v-if="isNewProfile" class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('lblPassword.Text') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                type="password"
                v-model="model.password"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div v-if="isNewProfile" class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('ConfirmNewPassword') | capitalize }}:
            </div>
            <validation-provider
              :rules="{
                required: true,
                is: model.password,
              }"
              v-slot="{ errors, failedRules }"
            >
              <custom-input
                type="password"
                v-model="model.confirmPassword"
                :error="errors[0]"
                custom-error-msg=""
              />
              <custom-error
                class="no-border"
                :message="`${$t('RequiredField')}.`"
                v-if="failedRules.required"
              />
              <custom-error
                class="no-border"
                :message="`${$t('PasswordsDoNotMatch')}.`"
                v-if="failedRules.is"
              />
            </validation-provider>
          </div>
        </div>
        <div v-if="isNewProfile" class="row mt-4">
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('SecurityQuestion') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-drop-down
                label=""
                placeholder="Seleccione"
                :options="options.securityQuestions"
                fieldkey="id"
                fieldname="name"
                v-model="model.question"
                @input="() => {}"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('SecurityAnswer') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.answer"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <!--General Info-->
      <div class="bg-white rounded p-4 mt-4">
        <div class="d-flex align-items-center">
          <em class="fal fa-ellipsis-h color-orange-light-2 mr-3 font-40" />
          <span class="color-dark-blue font-14 font-weight-bold">
            {{ $t('GeneralInfo') | upper }}
          </span>
        </div>
        <div class="row mt-2">
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Name') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.firstName"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('MidName') | capitalize }}:
            </div>
            <custom-input v-model="model.middleName" />
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('ApplicantLastName') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.lastName"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('ApplicantMaternalName') }}:
            </div>
            <custom-input v-model="model.maternalLastName" />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Telephone') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.telephone1"
                mask-type="Phone"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Mobile') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.telephone2"
                mask-type="Phone"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Gender') | capitalize }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-drop-down
                label=""
                placeholder="Seleccione"
                :options="options.genders"
                fieldkey="id"
                fieldname="name"
                v-model="model.gender"
                @input="() => {}"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('EmailAlt') | capitalize }}:
            </div>
            <validation-provider rules="required|email" v-slot="{ errors }">
              <custom-input
                v-model="model.emailAlt"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
        </div>
        <hr class="my-4" />
        <div class="row">
          <div class="col-md-4">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('AreEmployeeDepEdu') }}
            </div>
            <custom-radio-button
              id="rbIsEmployee"
              name="rbIsEmployee"
              class="m-2 rb__extended"
              is-inline
              v-model="model.isEDEmployee"
              :custom-error-msg="`${$t('RequiredField')}.`"
              :options="options.isEmployees"
            />
          </div>
          <div class="col-md-4">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('IsEstudent') }}
            </div>
            <custom-radio-button
              id="rbIsStudent"
              name="rbIsStudent"
              class="m-2 rb__extended"
              is-inline
              v-model="model.isStudent"
              :custom-error-msg="`${$t('RequiredField')}.`"
              :options="options.isStudent"
            />
          </div>
          <div class="col-md-4">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('IsWorking') }}
            </div>
            <custom-radio-button
              id="rbHasWork"
              name="rbHasWork"
              class="m-2 rb__extended"
              is-inline
              v-model="model.hasJob"
              :custom-error-msg="`${$t('RequiredField')}.`"
              :options="options.hasWork"
            />
          </div>
        </div>
        <div class="row mt-4" v-if="model.hasJob">
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('JobPosition') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.workPosition"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
          <div class="col-md-3">
            <div class="color-primary-4 font-14 font-weight-bold mb-1">
              {{ $t('Workplace') }}:
            </div>
            <validation-provider rules="required" v-slot="{ errors }">
              <custom-input
                v-model="model.workPlace"
                :error="errors[0]"
                :custom-error-msg="`${$t('RequiredField')}.`"
              />
            </validation-provider>
          </div>
        </div>
      </div>

      <!--Create / Update Date-->
      <div class="bg-white rounded p-4 mt-4">
        <div v-if="!isNewProfile" class="row">
          <div class="col-md-3">
            <div class="color-dark-blue font-16 font-weight-bold">
              {{ $t('CreatedDate') | capitalize }}:
            </div>
            <span class="color-black font-16">
              {{ model.createDate | datetime }}
            </span>
          </div>
          <div class="col-md-3">
            <div class="color-dark-blue font-16 font-weight-bold mb-1">
              {{ $t('LastModificationDate') | capitalize }}:
            </div>
            <span class="color-black font-16">
              {{ model.updatedDate | datetime }}
            </span>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
//third party
import _ from 'lodash';
import Checkbox from 'CheckBox';
//libs
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomRadioButton from 'custom-radio';
import CustomDropDown from 'custom-drop-down';
import CustomError from 'custom-error';

//services
import {
  getRoles,
  getInitCreateAccount as _getInitCreateAccount,
  createAccount as _createAccount,
  updateProfile as _updateProfile,
  resetUserPassword as _resetUserPassword,
  getUserRole,
  userStatus,
  disableUser,
  enableUser,
  confirmUser,
  unConfirmUser,
} from '@/services/app.api.js';

//mixins
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import { userMixin } from '@/mixins/user.mixin';

//utils
import { i18n, loadLanguageAsync } from '@/i18n.js';

//constants
import { genders as gendersOptions } from '@/utils/constants/genders.js';
import langTypes from '@/utils/constants/lang.js';
import { datetime } from '@/filters';

const YES_NO_OPTIONS = [
  { value: true, label: i18n.t('Yes') },
  { value: false, label: 'No' },
];

export default {
  name: 'CreateView',
  mixins: [alertMixin, userMixin],
  components: {
    ActionButton,
    CustomInput,
    CustomRadioButton,
    CustomDropDown,
    CustomError,
    Checkbox,
  },
  props: {
    isNewProfile: {
      type: Boolean,
      default: true,
    },
    userProfileInformation: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    model: {
      //user account
      email: null,
      emailConfirmed: false,
      active: false,
      role: null,
      password: null,
      confirmPassword: null,
      question: null,
      answer: null,
      roleName: '',
      //general information
      firstName: null,
      lastName: null,
      middleName: null,
      maternalLastName: null,
      telephone1: null,
      telephone2: null,
      gender: null,
      isStudent: false,
      isEDEmployee: false,
      hasJob: false,
      workPlace: null,
      workPosition: null,
      isResearchInAction: null,

      //investigation info
      schoolLevels: [],
      studySubjects: [],
      academicDegreeId: null,

      //physical address
      physicalAddress: {},

      //postal address
      postalAddress: {},
    },
    options: {
      isEmployees: [...YES_NO_OPTIONS],
      isStudent: [...YES_NO_OPTIONS],
      hasWork: [...YES_NO_OPTIONS],
      schoolLevels: [],
      studySubjects: [],
      studySubjects2: [],
      academicDegrees: [],
      cities: [],
      roles: [],
      genders: [...gendersOptions],
      securityQuestions: [],
    },
    profileInformation: {},
  }),
  computed: {
    isEnLang() {
      return this.userSelectedLang === langTypes.EN;
    },
  },
  async created() {
    loadLanguageAsync();
  },
  async mounted() {
    await this.getInitCreateAccount();
    //  console.log(this.options);
    //  console.log(role);
    //  this.model.roleName=role;
    //  console.log(this.model.roleName);

    //  await this.getUserStatus();
    //   //this.model.role = this.options.roles[0];

    //   //load user profile information
    //   this.onLoadUserProfileInformation();
  },
  methods: {
    datetime,
    async getInitCreateAccount() {
      const options = (await _getInitCreateAccount(this.isEnLang)).data;
      this.options = {
        ...this.options,
        ...options,
      };
      const roles = await getRoles();
      //  roles.map(role=> this.options.push({id:role.id, name:role.name}))
      this.options.roles = roles.data.filter(
        (role) => role.name != 'Solicitante'
      );
    },
    onSameAddressHandler(value) {
      this.model = {
        ...this.model,
        postalAddress: value
          ? { ...this.model.physicalAddress }
          : { ...this.model.postalAddress },
      };
    },
    async onSaveHandler() {
      const isValid = await this.$refs.formRef.validate();
      if (!isValid) {
        this.ShowToast(
          i18n.t('Alert'),
          `${i18n.t('PleaseCompleteRequestedInformation')}.`,
          'error'
        );
        return;
      }

      const payload = {
        ...this.model,
        question: this.model.question?.id,
        gender: this.model.gender?.id,
        academicDegreeId: this.model.academicDegreeId?.id,
        schoolLevels: [...this.model.schoolLevels],
        studySubjects: [...this.model.studySubjects],
        roleName: this.model.role.name,
        physicalAddress: {
          ...this.model.physicalAddress,
          cityId: this.model.physicalAddress.cityId?.id,
        },
        postalAddress: {
          ...this.model.postalAddress,
          cityId: this.model.postalAddress.cityId?.id,
        },
        isEn: this.isEnLang,
      };

      const result = await _createAccount(payload).catch((error) =>
        this.ShowToast('Error', error.response.data.message, 'error')
      );
      if (result) {
        const response = result.data;
        if (response.error) {
          this.ShowToast('Alerta', response.message, 'error');
          return;
        }
        this.ShowToast(
          i18n.t('ProcessSuccessfully'),
          i18n.t('AccountCreatedSuccessfully'),
          'success'
        );
        this.$router.go(-1);
      }
    },
    onLoadUserProfileInformation() {
      if (this.userId && !this.isNewProfile) {
        const profileInfo = this.userProfileInformation;
        this.model = {
          ...this.model,
          ...profileInfo,
          academicDegreeId: profileInfo.academicDegree,
          physicalAddress: {
            ...profileInfo.physicalAddress,
            cityId: profileInfo.physicalAddress?.city,
          },
          postalAddress: {
            ...profileInfo.postalAddress,
            cityId: profileInfo.postalAddress?.city,
          },
        };
      }
    },
    onChangeQuestionHandler() {
      this.$router.push({
        name: 'changeQuestion',
        params: {
          userId: this.$route.params.userId,
        },
      });
    },
    onChangeRoleHandler() {
      this.$router.push({
        name: 'changeRole',
        params: {
          userId: this.$route.params.userId,
        },
      });
    },
    async onResetPasswordHandler() {
      const answer = await this.ShowCancelModal(
        'Reinicio de Contraseña Usuario',
        '¿Está seguro que desea reiniciar la contraseña del usuario?',
        'Si',
        'No'
      );
      if (answer) {
        //CALL API for Reset Password
        _resetUserPassword(this.$route.params.userId).then((response) => {
          if (response.data.error) {
            this.ShowToast('Alerta', response.data.message, 'error');
            return;
          }
          this.ShowToast('Proceso exitoso', response.data.message, 'success');
        });
      }
    },
    updatStatus() {
      //     disableUser,
      // enableUser,
      // confirmUser,
      // unConfirmUser
      this.model.emailConfirmed
        ? confirmUser(this.$route.params.userId)
        : unConfirmUser(this.$route.params.userId);
      this.model.active
        ? enableUser(this.$route.params.userId)
        : disableUser(this.$route.params.userId);
    },
    async getUserStatus() {
      const result = (await userStatus(this.$route.params.userId)).data;
      this.model.active = result.active;
      this.model.emailConfirmed = result.emailConfirmed;
      console.log(result);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';

.rb__extended ::v-deep {
  .col-12 {
    margin-left: -24px !important;
  }
}
</style>
